// A RELOAD LOSES NOTHING A CLIENT TYPED (TW-0500, 2026-10-26).
//
// The form audit (docs/FORM-AUDIT-2026-10-26.md § 1): the page kept no draft, so when it reloaded under a client, or they left
// it and came back, they started again. 397 times in 48 days a browser started the form, left it unsigned and started it again
// at the same shop within 15 minutes, a median of 176 seconds after the first visit's last event; 103 of them never signed that
// day. On Chrome for Android it was 10.1% of every start: the browser drops a tab in the background while the client is in the
// camera or a message, and the page comes back empty.
//
// THE CALL (Grace, 2026-10-26, on the card; the audit asked for a privacy call first because of a shop's shared tablet):
//   * the draft lives in THIS TAB's own storage (sessionStorage), never on our servers and never on the device past the tab;
//   * it holds what was typed and ticked, and never a photo (the ID, the papers, the prescription, the lot numbers) or a
//     signature: none of them is in what this module is handed, which is the form's state of typed boxes and choices
//     (Form.tsx BLANK_FORM) and the ticks beside it, so nothing a stranger must not see can be drafted by any road;
//   * it is written as the client goes (a beat after the last change, and at once when the page is hidden), restored on load
//     when the key, the shop and the age fit, said in one line ("We kept what you typed.") with "Start over" beside it, and
//     gone on the signing, on "Start over", on a crash, and once it is 30 minutes past the client's last keystroke.
//
// WHAT IS NEVER DRAFTED, by name (NEVER_DRAFTED), beyond the photos and the signatures that never reach this module:
//   * the signed date: the day of a signing is the day it is signed, so a reload past midnight gets today's;
//   * the number on the client's ID (TW-0488, Maine's and Missouri's rule): a document's own number is the ID photo's kind of
//     thing, which the call keeps out;
//   * the two optional boxes, hearing from the shop and its news: an opt-in is the client's own tap on the page they sign. Both
//     start unticked, which the terms page's section 4 and every carrier filing say, and a reload must not make that untrue;
//   * and, beside the form's state (Form.tsx), the HIPAA photo release, an authorization the client may sign or leave alone.
//
// THE KEY IS THE SHOP AND THE FORM'S VERSION. The version is a fingerprint of the drafted state's shape (every key and the kind
// of value it holds, nested keys included: a field added, renamed or changing kind is a new version, with nothing to remember
// to bump) and of the bundle the page runs (its `main.<hash>.js`, so a deploy that changed a clause's words never puts a tick
// back beside words the client did not tick). A draft under any other key is never put back, and the next load removes it.
//
// COUNTED SINCE TW-0501. A draft put back beacons `form_draft_restored` (shop, language, service, never a value), once per load,
// so the win can be read; the name is on the beacon's two closed lists (beacon.ts FormEvent, the API's form_beacon.py NAMES).
//
// A SHOP IN HIPAA MODE KEEPS NO HEALTH ANSWER IN IT (TW-0501; Grace's call on TW-0500, 2026-10-26: "the tab on a shared tablet is
// the shop's device, the exposure is a client's health answers to the next client inside 30 minutes, and HIPAA's
// incidental-disclosure bar is higher than every other shop's; the rest of the draft stays, so HIPAA clients keep most of the
// win"). neverDraftedFor adds the questionnaire, every details box, the medical history and the medications, the client's
// physician and the scalp question to what is never drafted, and Form.tsx keeps the shop's own questions out of it too.
//
// AND IT IS SET ASIDE WHILE A POST IS OUT (TW-0501, Form.tsx pauseDraft): a confirmation lost on the way back must never leave
// a draft that a reload puts back to be signed a second time.
//
// AND IT CAN NEVER BREAK A SIGNING. Every call here is wrapped: storage that is full, blocked (a private window) or missing
// answers "no draft" and the form is the form it has always been.

/** 30 minutes after the client's last keystroke a draft is never put back (the card's number). */
export const DRAFT_KEEPS_MS = 30 * 60 * 1000;
/** The beat after the last change before the draft is written; a hidden page writes at once. */
export const DRAFT_WRITE_AFTER_MS = 400;
/** A draft stamped this far in the future (a phone's clock set back) is not trusted. */
const CLOCK_SLACK_MS = 60 * 1000;
/** Every draft key in a tab starts with this, so one sweep finds them all. */
export const DRAFT_PREFIX = "wt_draft:";
/** The draft's own envelope, bumped by hand only if its outer shape changes; the form's shape is fingerprinted. */
const ENVELOPE = "1";

/** The form's keys never drafted, by path (the header says why each). */
export const NEVER_DRAFTED: readonly string[] = [
  "signedDate",
  "idNumber",
  "acknowledgmentAndWaiver.hearFromShop",
  "acknowledgmentAndWaiver.shopMarketing",
];

/** TW-0501 — the health answers by path: what a shop in HIPAA mode's draft never holds, beside NEVER_DRAFTED. Every box of the
 *  blank form whose name ends "Details" is added to these by neverDraftedFor, so a details box added later is left out too. */
export const HEALTH_ANSWERS: readonly string[] = [
  "questionnaire",
  // TW-0526, the client-form side (2026-10-29) — the time the client last ate, Oklahoma's health box
  "lastAteAt",
  "medicalHistoryDetails",
  "medicationAndBloodThinners",
  "physicianName",
  "physicianPhone",
  "physicianAddress",
  "scalpCoversScars",
];

/** What a draft never holds for this shop: NEVER_DRAFTED, and for a shop in HIPAA mode every health answer too. Pure. */
export function neverDraftedFor(hipaa: boolean, blank: Record<string, unknown>): readonly string[] {
  if (!hipaa) return NEVER_DRAFTED;
  const details = Object.keys(blank).filter((key) => /Details$/.test(key));
  return Array.from(new Set([...NEVER_DRAFTED, ...HEALTH_ANSWERS, ...details]));
}

export type DraftStore = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key"> & { readonly length: number };

/** The tab's own storage, or null where the browser refuses it. */
export function tabStore(): DraftStore | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

type Plain = Record<string, unknown>;

const isPlain = (value: unknown): value is Plain => typeof value === "object" && value !== null && !Array.isArray(value);
const isStringList = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");
const kindOf = (value: unknown): string => (Array.isArray(value) ? "list" : typeof value);

/** Every key of `blank` and the kind of value it holds, nested keys by their path, in one order: the shape a draft must fit. */
export function shapeOf(blank: Plain, prefix = ""): string[] {
  const out: string[] = [];
  for (const key of Object.keys(blank).sort()) {
    const value = blank[key];
    if (isPlain(value)) out.push(...shapeOf(value, `${prefix}${key}.`));
    else out.push(`${prefix}${key}:${kindOf(value)}`);
  }
  return out;
}

/** FNV-1a over the text, as a short word: a fingerprint, never a secret. */
function fingerprint(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

/** The content hash of the bundle this page runs (`/static/js/main.<hash>.js`), or "" where there is none to read: the dev
 *  server's `bundle.js`, and the test runner. */
export function bundleId(doc: Document | undefined = typeof document === "undefined" ? undefined : document): string {
  try {
    const scripts = doc ? Array.from(doc.getElementsByTagName("script")) : [];
    for (const script of scripts) {
      const found = /\/static\/js\/main\.([a-z0-9]+)\.js(?:[?#]|$)/.exec(script.getAttribute("src") || "");
      if (found) return found[1];
    }
  } catch {
    /* no document to read: the shape alone is the version */
  }
  return "";
}

/** The form's version: its shape and the bundle it runs in, as one word. */
export function formVersion(blank: Plain, bundle: string = bundleId()): string {
  return fingerprint(`${ENVELOPE}|${shapeOf(blank).join(",")}|${bundle}`);
}

export const draftKey = (shop: string, version: string): string => `${DRAFT_PREFIX}${shop}:${version}`;

/** What differs from the blank form, and only that, nested keys as nested objects: what the client has typed and ticked.
 *  A key on `never` (by path) is left out whatever it holds. */
export function draftOf(current: Plain, blank: Plain, never: readonly string[] = NEVER_DRAFTED, prefix = ""): Plain {
  const out: Plain = {};
  for (const key of Object.keys(blank)) {
    const path = `${prefix}${key}`;
    if (never.includes(path)) continue;
    const was = blank[key];
    const now = current[key];
    if (isPlain(was)) {
      if (!isPlain(now)) continue;
      const inner = draftOf(now, was, never, `${path}.`);
      if (Object.keys(inner).length) out[key] = inner;
    } else if (Array.isArray(was)) {
      if (isStringList(now) && now.join("\u001f") !== (was as unknown[]).join("\u001f")) out[key] = now.slice();
    } else if (typeof now === typeof was && now !== was) {
      out[key] = now;
    }
  }
  return out;
}

/** `current` with the drafted values put back: only a key the blank form has, only a value of the kind it holds there, and
 *  never a key on `never`, whatever the stored draft says. `restored` counts the values put back. */
export function withDraft<T extends Plain>(
  current: T,
  blank: Plain,
  drafted: unknown,
  never: readonly string[] = NEVER_DRAFTED,
  prefix = ""
): { next: T; restored: number } {
  if (!isPlain(drafted)) return { next: current, restored: 0 };
  let restored = 0;
  const next: Plain = { ...current };
  for (const key of Object.keys(blank)) {
    const path = `${prefix}${key}`;
    if (never.includes(path) || !Object.prototype.hasOwnProperty.call(drafted, key)) continue;
    const was = blank[key];
    const value = drafted[key];
    if (isPlain(was)) {
      const base = isPlain(current[key]) ? (current[key] as Plain) : was;
      const inner = withDraft(base, was, value, never, `${path}.`);
      if (inner.restored) {
        next[key] = inner.next;
        restored += inner.restored;
      }
    } else if (Array.isArray(was)) {
      if (isStringList(value)) {
        next[key] = value.slice();
        restored += 1;
      }
    } else if (typeof value === typeof was) {
      next[key] = value;
      restored += 1;
    }
  }
  return { next: next as T, restored };
}

/** The ticks of a map whose keys are ours (a notice's id, a clause's), true ones only. */
export function ticksOf(value: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!isPlain(value)) return out;
  for (const key of Object.keys(value)) {
    if (value[key] === true && /^[A-Za-z0-9_.:-]{1,80}$/.test(key)) out[key] = true;
  }
  return out;
}

/** The answers of a map whose keys are ours, each one of `allowed`. */
export function answersOf(value: unknown, allowed: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isPlain(value)) return out;
  for (const key of Object.keys(value)) {
    const answer = value[key];
    if (typeof answer === "string" && allowed.includes(answer) && /^[A-Za-z0-9_.:-]{1,80}$/.test(key)) out[key] = answer;
  }
  return out;
}

/** The typed parts of a map (the price's boxes), each a string, empty ones left out. */
export function partsOf(value: unknown, keys: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isPlain(value)) return out;
  for (const key of keys) {
    const part = value[key];
    if (typeof part === "string" && part) out[key] = part;
  }
  return out;
}

/** One stored draft: the form's version and the shop it was written for, the shop's state then, the client's last keystroke,
 *  and the values (Form.tsx says which). */
export type Draft = { v: string; shop: string; state: string; at: number; values: Plain };

/** Is it under 30 minutes since the client's last keystroke? */
export function isFresh(draft: Pick<Draft, "at">, now: number = Date.now()): boolean {
  const age = now - draft.at;
  return Number.isFinite(age) && age >= -CLOCK_SLACK_MS && age < DRAFT_KEEPS_MS;
}

function parse(raw: string): Draft | null {
  const value: unknown = JSON.parse(raw);
  if (!isPlain(value)) return null;
  const { v, shop, state, at, values } = value;
  if (typeof v !== "string" || typeof shop !== "string" || typeof state !== "string" || typeof at !== "number" || !isPlain(values)) {
    return null;
  }
  return { v, shop, state, at, values };
}

/** Every draft key in the tab but `keep`: another version's, or another shop's on a host that serves them all (a laptop). */
function sweep(store: DraftStore, keep: string): void {
  const stale: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && key.startsWith(DRAFT_PREFIX) && key !== keep) stale.push(key);
  }
  for (const key of stale) store.removeItem(key);
}

/** This tab's draft for this shop and this version of the form, if it is whole and fresh; anything else under the key, and
 *  every other draft in the tab, is removed on the way. Never throws. */
export function readDraft(shop: string, version: string, now: number = Date.now(), store: DraftStore | null = tabStore()): Draft | null {
  if (!store || !shop) return null;
  const key = draftKey(shop, version);
  try {
    sweep(store, key);
    const raw = store.getItem(key);
    if (!raw) return null;
    const draft = parse(raw);
    if (draft && draft.v === version && draft.shop === shop && isFresh(draft, now)) return draft;
    store.removeItem(key);
    return null;
  } catch {
    forgetDraft(shop, version, store);
    return null;
  }
}

/** Write it, or nothing: a full or refusing store leaves the form exactly as it is. */
export function writeDraft(draft: Draft, store: DraftStore | null = tabStore()): boolean {
  if (!store || !draft.shop) return false;
  try {
    store.setItem(draftKey(draft.shop, draft.v), JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function forgetDraft(shop: string, version: string, store: DraftStore | null = tabStore()): void {
  try {
    store?.removeItem(draftKey(shop, version));
  } catch {
    /* nothing to remove, or a store that refuses: either way nothing is put back from it */
  }
}

/** Every draft in the tab, gone: a crash, so that "Reload and try again" opens a form with nothing in it. */
export function forgetEveryDraft(store: DraftStore | null = tabStore()): void {
  try {
    if (store) sweep(store, "");
  } catch {
    /* a store that refuses holds nothing this page can read back either */
  }
}

/** The reload "Start over" ends in, on an object a test can stub (jsdom has no navigation; topUp.ts's `page` is the same). */
export const draftPage = { reload: (): void => window.location.reload() };
